// app/javascript/controllers/text_limit_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "counter"]
  static values = { 
    limit: Number, 
    isRichText: { type: Boolean, default: false }
  }

  connect() {
    // Identifier l'élément à surveiller selon qu'il s'agit d'un rich_text ou non
    if (this.isRichTextValue) {
      this.editor = this.inputTarget.querySelector("trix-editor")
      this.editor.addEventListener("trix-change", this.updateCounter.bind(this))
      this.editor.addEventListener("trix-change", this.enforceLimit.bind(this))
    } else {
      // Écouter tous les événements de changement de contenu
      this.inputTarget.addEventListener("input", this.updateCounter.bind(this))
      this.inputTarget.addEventListener("input", this.enforceLimit.bind(this))
      this.inputTarget.addEventListener("paste", this.handlePaste.bind(this))
      this.inputTarget.addEventListener("cut", this.handleCutOrDelete.bind(this))
      this.inputTarget.addEventListener("keydown", this.handleKeydown.bind(this))
    }
    
    // Initialiser le compteur
    this.updateCounter()
  }

  disconnect() {
    // Nettoyer les event listeners
    if (this.isRichTextValue && this.editor) {
      this.editor.removeEventListener("trix-change", this.updateCounter.bind(this))
      this.editor.removeEventListener("trix-change", this.enforceLimit.bind(this))
    } else {
      this.inputTarget.removeEventListener("input", this.updateCounter.bind(this))
      this.inputTarget.removeEventListener("input", this.enforceLimit.bind(this))
      this.inputTarget.removeEventListener("paste", this.handlePaste.bind(this))
      this.inputTarget.removeEventListener("cut", this.handleCutOrDelete.bind(this))
      this.inputTarget.removeEventListener("keydown", this.handleKeydown.bind(this))
    }
  }

  handlePaste(event) {
    // Attendre que le paste soit effectué
    setTimeout(() => {
      this.enforceLimit()
      this.updateCounter()
    }, 0)
  }

  handleCutOrDelete(event) {
    // Attendre que la suppression soit effectuée
    setTimeout(() => {
      this.updateCounter()
    }, 0)
  }

  handleKeydown(event) {
    // Gérer les raccourcis clavier (Ctrl+V, Ctrl+X, etc.)
    if (event.ctrlKey || event.metaKey) {
      setTimeout(() => {
        this.enforceLimit()
        this.updateCounter()
      }, 0)
    }
  }
  
  updateCounter() {
    const count = this.getCurrentTextLength()
    const remaining = this.limitValue - count
    
    this.counterTarget.textContent = `${count}/${this.limitValue} caractères`
    
    // Styling du compteur
    if (count >= this.limitValue) {
      this.counterTarget.classList.add("text-red-600", "font-bold")
    } else {
      this.counterTarget.classList.remove("text-red-600", "font-bold")
    }
    
    // Styling du champ input
    if (count >= this.limitValue) {
      this.inputTarget.classList.add("border-red-500", "bg-red-200")
      this.inputTarget.classList.remove("border-gray-300", "focus:border-blue-500")
    } else {
      this.inputTarget.classList.remove("border-red-500", "bg-red-200")
      this.inputTarget.classList.add("border-gray-300", "focus:border-blue-500")
    }
  }
  
  enforceLimit() {
    const count = this.getCurrentTextLength()
    
    if (count > this.limitValue) {
      if (this.isRichTextValue) {
        // Pour les éditeurs rich text
        const originalRange = this.editor.editor.getSelectedRange()
        const exceededChars = count - this.limitValue
        
        // Sélectionner les derniers caractères excédentaires et les supprimer
        const document = this.editor.editor.getDocument()
        const textLength = document.toString().length
        
        this.editor.editor.setSelectedRange([textLength - exceededChars, textLength])
        this.editor.editor.deleteInDirection("backward")
        
        // Essayer de restaurer la position du curseur
        if (originalRange[0] <= this.limitValue) {
          this.editor.editor.setSelectedRange([
            Math.min(originalRange[0], this.limitValue),
            Math.min(originalRange[1], this.limitValue)
          ])
        }
      } else {
        // Pour les champs texte standard
        const cursorPosition = this.inputTarget.selectionStart
        this.inputTarget.value = this.inputTarget.value.substring(0, this.limitValue)
        // Repositionner le curseur
        this.inputTarget.setSelectionRange(
          Math.min(cursorPosition, this.limitValue),
          Math.min(cursorPosition, this.limitValue)
        )
      }
      
      this.updateCounter()
    }
  }
  
  getCurrentTextLength() {
    if (this.isRichTextValue) {
      return this.stripHtml(this.editor.editor.getDocument().toString()).length
    } else {
      return this.inputTarget.value.length
    }
  }
  
  stripHtml(html) {
    const tmp = document.createElement("DIV")
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ""
  }
}